import { isMalformedAuctionsNearMeUrl } from '@/pages/AuctionsNearMe/helpers/isMalformedAuctionsNearMeUrl';
import { getArticle, getArticleError, getArticleLoaded } from '@/redux/modules/article/article.selectors';
import { getInvalidLocationPathSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { getItemSummaryCatalogId, getItemSummaryIsDeleted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSlugItemId } from '@/redux/modules/priceResults/priceResults.selectors';
import { GlobalState } from '@/redux/store';
import {
    getItemIdFromPath,
    isAuctionsNearMePage,
    isGuideArticlePage,
    isItemPage,
    isPriceResultsPage,
    normalizeSoft404Path,
} from '@/utils/urls';

const isItemPageSoft404 = (path: string, state: GlobalState) => {
    if (!isItemPage(path)) {
        return false;
    }

    const itemId = getItemIdFromPath(path);
    const catalogId = getItemSummaryCatalogId(state, itemId);
    const isDeleted = getItemSummaryIsDeleted(state, itemId);

    return !catalogId || isDeleted;
};

const isPriceResultsPageSoft404 = (path: string, state: GlobalState) => {
    if (!isPriceResultsPage(path)) {
        return false;
    }

    return !getSlugItemId(state);
};

const isGuideArticlePageSoft404 = (path: string, state: GlobalState) => {
    if (!isGuideArticlePage(path)) {
        return false;
    }

    const isLoaded = getArticleLoaded(state);
    const article = getArticle(state);
    const error = getArticleError(state);

    // Clean miss only — unexpected fetch errors stay on the article error UI.
    return isLoaded && !article && !error;
};

export const isAuctionsNearMePageSoft404 = (path: string, state: GlobalState) => {
    if (!isAuctionsNearMePage(path)) {
        return false;
    }

    // Decidable from the URL alone, so it holds on the client's first render — before any loader
    // has run, and without the SSR state the page would otherwise have to be handed.
    if (isMalformedAuctionsNearMeUrl(path)) {
        return true;
    }

    return isSoft404PathActive(getInvalidLocationPathSelector(state), path);
};

/**
 * Soft-404 predicate for item, price-result, guide article, and Auctions Near Me pages.
 * Keeps the original URL and lets SSR/client render NotFound with HTTP 404.
 */
export const isSoft404Page = (path: string, state: GlobalState) =>
    isItemPageSoft404(path, state) ||
    isPriceResultsPageSoft404(path, state) ||
    isGuideArticlePageSoft404(path, state) ||
    isAuctionsNearMePageSoft404(path, state);

/**
 * A soft-404 verdict must only apply while the user remains on the path that produced it.
 * Otherwise SPA navigation after a soft-404 stays trapped on NotFound.
 */
export const isSoft404PathActive = (soft404Path: string | null | undefined, currentPath: string): boolean => {
    if (!soft404Path) {
        return false;
    }

    return normalizeSoft404Path(soft404Path) === normalizeSoft404Path(currentPath);
};
